import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { UpdateMaintenanceDialog } from "@/components/maintenance/UpdateMaintenanceDialog";
import { format, isValid } from "date-fns";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { UpdateMaintenanceHistoryDialog } from "@/components/maintenance/UpdateMaintenanceHistoryDialog";

function RideLink({ bookingId }: { bookingId: string }) {
  const [rideId, setRideId] = React.useState<string | null>(null);
  React.useEffect(() => {
    let isMounted = true;
    async function fetchRide() {
      const { data, error } = await supabase
        .from("rides")
        .select("id")
        .eq("booking_id", bookingId)
        .limit(1)
        .single();
      if (isMounted && data?.id) setRideId(data.id);
    }
    fetchRide();
    return () => { isMounted = false; };
  }, [bookingId]);
  if (!rideId) return null;
  return (
    <span className="text-sm">
      Ride: <Link to={`/rides/${rideId}`} className="text-blue-600 underline">View Ride</Link>
    </span>
  );
}

const MaintenanceIssueDetailPage: React.FC = () => {
  const { issueId } = useParams<{ issueId: string }>();
  const navigate = useNavigate();

  const {
    data: issue,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["maintenance-issue", issueId],
    queryFn: async () => {
      if (!issueId) return null;
      const { data, error } = await supabase
        .from("maintenance_issues")
        .select(`*, bikes (id, serial_number, status), reported_by (id, username)`) // add more fields as needed
        .eq("id", issueId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!issueId,
  });

  const {
    data: history,
    isLoading: historyLoading,
    error: historyError,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: ["maintenance-issue-history", issueId],
    queryFn: async () => {
      if (!issueId) return [];
      const { data, error } = await supabase
        .from("maintenance_issues_history")
        .select(`*, bike:bike_id (serial_number), issue:issue_id (booking:booking_id (start_location_id, end_location_id, ride_type)), changed_by:changed_by (id, username)`)
        .eq("issue_id", issueId)
        .order("change_timestamp", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!issueId,
  });

  // Fetch all sites for ID-to-name mapping
  const { data: sites, isLoading: isLoadingSites } = useQuery({
    queryKey: ["sites"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sites")
        .select("id, name");
      if (error) throw error;
      return data || [];
    },
  });

  // Helper to get site name by ID
  const getSiteName = (siteId: string | null | undefined) => {
    if (!siteId || !sites) return "N/A";
    const site = sites.find((s: any) => s.id === siteId);
    return site ? site.name : siteId;
  };

  const getBadgeColor = (status: string) => {
    switch (status) {
      case "reported":
        return "bg-yellow-100 text-yellow-800";
      case "in_progress":
        return "bg-blue-100 text-blue-800";
      case "resolved":
        return "bg-green-100 text-green-800";
      case "broken":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getIssueTypeDisplay = (type: string) => {
    switch (type) {
      case "battery":
        return "Battery Issue";
      case "missing":
        return "Missing Bike";
      case "unreported":
        return "Unreported";
      case "other":
      default:
        return "Other Issue";
    }
  };

  const timeAgo = issue?.reported_at
    ? (() => {
        const date = new Date(issue.reported_at);
        return isValid(date) && date.getFullYear() > 1970
          ? format(date, "MMM do, yyyy | h:mm a")
          : "Recently";
      })()
    : "Unknown time";

  // Fetch the user for the booking associated with this issue
  const { data: bookingUser, isLoading: isLoadingBookingUser } = useQuery({
    queryKey: ["booking-user", issue?.booking_id],
    queryFn: async () => {
      if (!issue?.booking_id) return null;
      const { data, error } = await supabase
        .from("bookings")
        .select("user_id, users: user_id (id, username)")
        .eq("id", issue.booking_id)
        .single();
      if (error) throw error;
      return data?.users || null;
    },
    enabled: !!issue?.booking_id,
  });

  // Fetch the user and route for the booking associated with this issue
  const { data: bookingDetails, isLoading: isLoadingBookingDetails } = useQuery({
    queryKey: ["booking-details", issue?.booking_id],
    queryFn: async () => {
      if (!issue?.booking_id) return null;
      const { data, error } = await supabase
        .from("bookings")
        .select("start_location_id, end_location_id, ride_type")
        .eq("id", issue.booking_id)
        .single();
      if (error) throw error;
      return data || null;
    },
    enabled: !!issue?.booking_id,
  });

  if (isLoading) {
    return <div className="p-8 text-center">Loading maintenance issue...</div>;
  }

  if (error || !issue) {
    return (
      <div className="p-8 text-center text-red-500">
        Failed to load maintenance issue.<br />
        <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-8 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Maintenance Issue Details</h1>
        <Button variant="outline" asChild>
          <Link to={`/bikes/${issue.bikes?.id}?tab=maintenance`}>
            Back to Bike
          </Link>
        </Button>
      </div>
      <div className="border rounded-lg p-6 bg-white shadow">
        <div className="flex justify-between">
          <div>
            <h3 className="font-medium text-lg mb-1">
              Bike: {issue.bikes?.serial_number}
            </h3>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline">{getIssueTypeDisplay(issue.issue_type)}</Badge>
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                Reported by: {issue.reported_by?.id && issue.reported_by?.username ? (
                  <Link to={`/users/${issue.reported_by.id}`} className="text-blue-600 underline hover:text-blue-800">
                    {issue.reported_by.username}
                  </Link>
                ) : (
                  'N/A'
                )}
              </span>
              {/* Cost (only in main card, not summed) */}
              {issue.cost !== null && issue.cost !== undefined && (
                <span className="ml-4 px-2 py-0.5 rounded bg-green-50 text-green-800 font-semibold text-xs border border-green-200" style={{marginTop: 2}}>
                  Cost: £{issue.cost}
                </span>
              )}
            </div>
            {/* Booking and Ride Links */}
            {issue.booking_id && (
              <div className="flex flex-col gap-1 mt-2">
                <span className="text-sm">
                  {bookingUser && (
                    <span className="flex items-center gap-2 mt-1">
                      <span className="font-semibold text-gray-700">User:</span>
                      <Link
                        to={`/users/${bookingUser.id}`}
                        className="font-bold text-green-800 underline hover:text-green-900 transition-colors"
                      >
                        {bookingUser.username}
                      </Link>
                    </span>
                  )}
                </span>
                {/* Route display */}
                {bookingDetails && (bookingDetails.start_location_id || bookingDetails.end_location_id) && (
                  <div className="mt-1 flex items-center gap-2 text-sm">
                    <span className="font-semibold text-gray-700">Route:</span>
                    <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-800 font-medium">{getSiteName(bookingDetails.start_location_id)}</span>
                    <span className="text-gray-400">→</span>
                    <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-800 font-medium">{getSiteName(bookingDetails.end_location_id)}</span>
                    {bookingDetails.ride_type && (
                      <span className="ml-2 px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-semibold uppercase tracking-wide text-xs">{bookingDetails.ride_type}</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="text-right">
            <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium capitalize ${getBadgeColor(issue.status)}`}>
              {issue.status}
            </span>
            <p className="text-sm text-muted-foreground mt-1">{timeAgo}</p>
          </div>
        </div>
        <p className="mt-4 text-sm text-muted-foreground">{issue.notes}</p>
        <div className="mt-6 flex justify-end">
          <UpdateMaintenanceDialog issue={issue} onUpdate={refetch} />
        </div>
      </div>

      {/* Issue History Section */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold mb-2">Issue History</h2>
        {historyLoading ? (
          <div>Loading history...</div>
        ) : historyError ? (
          <div className="text-red-500">Failed to load history.</div>
        ) : !history || history.length === 0 ? (
          <div className="text-muted-foreground">No history found for this issue.</div>
        ) : (
          <div className="space-y-4">
            {history.map((entry: any) => (
              <div key={entry.id} className="border rounded p-4 bg-gray-50">
                <div className="flex justify-between">
                  <div>
                    <div className="font-medium">
                      Bike: {entry.bike?.serial_number || entry.bike_id}
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                      Changed by: {entry.changed_by?.id && entry.changed_by?.username ? (
                        <Link to={`/users/${entry.changed_by.id}`} className="text-blue-600 underline hover:text-blue-800">
                          {entry.changed_by.username}
                        </Link>
                      ) : (
                        'Unknown'
                      )}
                    </div>
                    {/* Booking info */}
                    {(() => {
                      // Try to extract booking info from the joined data
                      const booking = entry.issue?.booking;
                      if (booking && (booking.start_location_id || booking.end_location_id)) {
                        return (
                          <div className="mt-1 text-xs flex items-center gap-2">
                            <span className="font-semibold">Route:</span>
                            <span>{getSiteName(booking.start_location_id)}</span>
                            <span>→</span>
                            <span>{getSiteName(booking.end_location_id)}</span>
                            {booking.ride_type && (
                              <span className="ml-2 px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-semibold">{booking.ride_type}</span>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="text-xs text-gray-500">
                      {entry.change_timestamp && (() => {
                        const date = new Date(entry.change_timestamp);
                        return isValid(date) && date.getFullYear() > 1970
                          ? format(date, "MMM do, yyyy | h:mm a")
                          : "Unknown time";
                      })()}
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-sm">
                  Status: <span className="font-semibold">{entry.status}</span>
                </div>
                {/* Show cost from history entry in each history card */}
                {entry.cost !== null && entry.cost !== undefined && (
                  <div className="mt-1 text-sm text-green-700 font-medium">
                    Cost: £{entry.cost}
                  </div>
                )}
                <div className="mt-1 text-sm text-muted-foreground">
                  {entry.notes}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MaintenanceIssueDetailPage; 